/// Constants for subsidy calculation

/** Basiswert */
export const baseValue = (year: number): number =>
  Base[year as keyof typeof Base] ?? Object.values(Base).at(-1) ?? NaN

/** Qualitätsbonus */
export const qualityBonus = (year: number): number =>
  Quality[year as keyof typeof Quality] ?? Object.values(Quality).at(-1) ?? NaN

/** Ausbaufaktor */
export const expansionFactor = (year: number): number =>
  Expansion[year as keyof typeof Expansion] ?? Object.values(Expansion).at(-1) ?? NaN

/**
 * Mindestanstellungsschlüssel (amount of (weighted) children an employee has to care for)
 * Satisfied if below or equal to this value (11.0).
 */
export const minimumStaffingRatio = 11.0 // could change in the future

/** Mindestfachkraftquote */
export const minimumFkq = 50.0

export const subsidyForParentalFee = 100 // €

/** Basiswert (Kindertageseinrichtungen Kommunal) */
const Base = {
  2018: 1161.65,
  2019: 1197.93,
  2020: 1229.11,
  2021: 1237.03,
  2022: 1288.48,
  2023: 1375.88,
  2024: 1458.98,
  2025: 1521.62,
  2026: 1563.88,
} as const

/** Qualitätsbonus */
const Quality = {
  2018: 61.03,
  2019: 62.93,
  2020: 64.57,
  2021: 64.99,
  2022: 67.69,
  2023: 72.28,
  2024: 76.65,
  2025: 79.94,
  2026: 268.01,
} as const

/** Ausbaufaktor */
const Expansion = {
  2018: 0.732,
  2019: 0.503,
  2020: 0.523,
  2021: 0.525,
  2022: 0.436,
  2023: 0.384,
  2024: 0.33,
  2025: 0.288,
} as const

/** Förderstatus */
export enum SubsidyState {
  /** Kein Status (nur für "Keine Belegung") */
  nil = "",
  /** Kind - Regelförderung */
  X00 = "X00",
  /** Kind - Schulkind */
  X01 = "X01",
  /** Kind - 0 bis unter 3 */
  X02 = "X02",
  /** Migration - Regelförderung */
  X03 = "X03",
  /** Migration - Schulkind */
  X04 = "X04",
  /** Migration - 0 bis unter 3 */
  X05 = "X05",
  /** Behinderung - Regelförderung */
  X06 = "X06",
  /** Behinderung - Schulkind */
  X07 = "X07",
  /** Behinderung - 0 bis unter 3 */
  X08 = "X08",
  /** Behinderung - Migration */
  X09 = "X09",
}

export function isIntegrative(subsidyState: SubsidyState) {
  return (
    subsidyState === SubsidyState.X06 ||
    subsidyState === SubsidyState.X07 ||
    subsidyState === SubsidyState.X08 ||
    subsidyState === SubsidyState.X09
  )
}

/** Buchungszeit */
export enum BookingTime {
  /** "Keine Zeit" / "Keine Belegung" / <= 1 Stunde */
  nil = "",
  /** \>1‒2 Stunden */
  Y00 = "Y00",
  /** \>2‒3 Stunden */
  Y01 = "Y01",
  /** \>3‒4 Stunden */
  Y02 = "Y02",
  /** \>4‒5 Stunden */
  Y03 = "Y03",
  /** \>5‒6 Stunden */
  Y04 = "Y04",
  /** \>6‒7 Stunden */
  Y05 = "Y05",
  /** \>7‒8 Stunden */
  Y06 = "Y06",
  /** \>8‒9 Stunden */
  Y07 = "Y07",
  /** \>9 Stunden */
  Y08 = "Y08",
}

export function numberToBookingTime(hours: number) {
  if (hours <= 1) {
    return BookingTime.nil
  } else if (hours > 9) {
    return BookingTime.Y08
  } else if (hours > 8) {
    return BookingTime.Y07
  } else if (hours > 7) {
    return BookingTime.Y06
  } else if (hours > 6) {
    return BookingTime.Y05
  } else if (hours > 5) {
    return BookingTime.Y04
  } else if (hours > 4) {
    return BookingTime.Y03
  } else if (hours > 3) {
    return BookingTime.Y02
  } else if (hours > 2) {
    return BookingTime.Y01
  } else if (hours > 1) {
    return BookingTime.Y00
  } else {
    // @ts-expect-error until tsc can understand this
    const exhaustiveCheck: never = hours
    throw `Invalid average booking time: ${exhaustiveCheck as number}`
  }
}

/** angerechnete Buchungsstunden */
export const BookingTimeHours = {
  [BookingTime.nil]: 0,
  [BookingTime.Y00]: 2,
  [BookingTime.Y01]: 3,
  [BookingTime.Y02]: 4,
  [BookingTime.Y03]: 5,
  [BookingTime.Y04]: 6,
  [BookingTime.Y05]: 7,
  [BookingTime.Y06]: 8,
  [BookingTime.Y07]: 9,
  [BookingTime.Y08]: 10,
} as const

/** Gewichtungsfaktoren */
export const SubsidyStateFactors = {
  [SubsidyState.nil]: 0,
  [SubsidyState.X00]: 1.0,
  [SubsidyState.X01]: 1.2,
  [SubsidyState.X02]: 2.0,
  [SubsidyState.X03]: 1.3,
  [SubsidyState.X04]: 1.3,
  [SubsidyState.X05]: 2.0,
  [SubsidyState.X06]: 4.5,
  [SubsidyState.X07]: 4.5,
  [SubsidyState.X08]: 4.5,
  [SubsidyState.X09]: 4.5,
} as const

/** Zeitfaktoren */
export const BookingTimeFactors = {
  [BookingTime.nil]: 0,
  [BookingTime.Y00]: 0.5,
  [BookingTime.Y01]: 0.75,
  [BookingTime.Y02]: 1,
  [BookingTime.Y03]: 1.25,
  [BookingTime.Y04]: 1.5,
  [BookingTime.Y05]: 1.75,
  [BookingTime.Y06]: 2.0,
  [BookingTime.Y07]: 2.25,
  [BookingTime.Y08]: 2.5,
} as const

export const RegionMFBookingTimeFactorConstantAddend = 6

export const SubsidyStateStrings = {
  [SubsidyState.nil]: "",
  [SubsidyState.X00]: "R",
  [SubsidyState.X01]: "S",
  [SubsidyState.X02]: "U",
  [SubsidyState.X03]: "M",
  [SubsidyState.X04]: "MS",
  [SubsidyState.X05]: "MU",
  [SubsidyState.X06]: "B",
  [SubsidyState.X07]: "BS",
  [SubsidyState.X08]: "BU",
  [SubsidyState.X09]: "BM",
} as const

const stringsToSubsidyState = Object.fromEntries(
  Object.entries(SubsidyStateStrings).map(([k, v]) => [v, k as SubsidyState])
)

const hoursToBookingTime = Object.fromEntries(
  Object.entries(BookingTimeHours).map(([k, v]) => [v, k as BookingTime])
)

export function formatBookingCell(bt: BookingTime, ss: SubsidyState): string {
  if (ss === SubsidyState.nil || bt === BookingTime.nil) return ""
  const h = BookingTimeHours[bt]
  return `>${h - 1}-${h} ${SubsidyStateStrings[ss]}`
}

export function parseBookingCell(
  val: string
): { bookingTime: BookingTime; subsidyState: SubsidyState } | null {
  const trimmed = val.trim()
  if (!trimmed) return { bookingTime: BookingTime.nil, subsidyState: SubsidyState.nil }
  const spaceIndex = trimmed.lastIndexOf(" ")
  if (spaceIndex === -1) return null
  const hoursPart = trimmed.slice(0, spaceIndex)
  const stateStr = trimmed.slice(spaceIndex + 1)
  const dashIdx = hoursPart.lastIndexOf("-")
  if (dashIdx === -1) return null
  const hours = parseInt(hoursPart.slice(dashIdx + 1))
  if (isNaN(hours)) return null
  const bookingTime = hoursToBookingTime[hours]
  const ss = stringsToSubsidyState[stateStr]
  if (!bookingTime || ss === undefined) return null
  return { bookingTime, subsidyState: ss }
}

/** AVKiBiG § 17(3) sentence 4 (feature disabled for now) */
export const UnmetMonthAllowance = 0

export enum MonthSubsidyStatus {
  /** Month likely won't be getting subsidized (except for certain edge cases, e.g., § 17 (3) 6 AVKiBiG) */
  NO_SUBSIDY,
  /** Month will still be subsizided under § 17 (3) 5 AVKiBiG. */
  EXCEPTION_17_3_5,
  /** Month will be subsizided */
  SUBSIDIZED,
}

export enum Criteria {
  no_children = "no_children",
  not_enough_booking_hours = "not_enough_booking_hours",
  insufficient_as = "insufficient_as",
  insufficient_fkq = "insufficient_fkq",
  exception_17_3_5 = "exception_17_3_5",
  not_enough_integrative_children_45x = "not_enough_integrative_children_45x",
}

export type MonthStatus = {
  subsidyStatus: MonthSubsidyStatus
  reason: Criteria
}

export enum InfoMessage {
  averages_row = "averages_row",
  averages_row2 = "averages_row2",
}
